import { UseFormReturnType } from '@mantine/form';
import { createContext, Dispatch, SetStateAction, useContext } from 'react';

export interface I_IconSetting {
  type: 'system' | 'custom'; // 系统 或 自定义
  // 系统图标
  index?: number;
  // 自定义图标
  url?: string;
  file?: File;
}

export type SignatureMessagesDisplayType = 'all' | 'widget' | 'share' | 'desktop';

export interface I_UserFormValues {
  chatbotIcon: {
    icon: I_IconSetting;
    position: {
      checked: boolean;
      value: 'left' | 'right';
    };
    customPosition: {
      checked: boolean;
      x: number;
      y: number;
    };
    color: {
      checked: boolean;
      value: string;
    };
    stayPage: {
      checked: boolean;
      value: number;
    };
    iconButtonText: {
      checked: boolean;
      value: string;
    };
    iconButtonSize: {
      checked: boolean;
      value: number;
    };
  };
  chatbot: {
    multiLanguageTitle: {
      checked: boolean;
      values: {
        zh: string;
        en: string;
        ja: string;
        ko: string;
      };
    };
    welcomeMessage: {
      checked: boolean;
      value: string;
      type: string;
      multiLanguageValues: {
        zh: string;
        en: string;
        ja: string;
        ko: string;
      };
    };
    suggestedMessages: {
      checked: boolean;
      value: string;
      type: string;
      node_list: string[];
      // 新增：保存节点详细信息，包含 dataset_id
      node_details?: Array<{
        id: string;
        dataset_id: string;
      }>;
      // 新的穿梭框模式数据结构
      selectedDatasetId?: string; // 单一选择的知识库ID
      selectedNodeIds?: string[]; // 排序后的节点ID列表
      // 自定义引导文案配置
      greetingMessage?: {
        checked: boolean; // 是否启用自定义引导文案
        value: string; // 自定义引导文案内容，空字符串表示不显示
      };
    };
    signatureMessages: {
      checked: boolean;
      value: string;
      displayType: SignatureMessagesDisplayType;
      list: {
        iconIndex: number;
        value: string;
      }[];
    };
    chatHeaderDisplay: {
      checked: boolean;
      value: string;
    };
    inputPlaceholder: {
      checked: boolean;
      value: string;
    };
    securityNotice: {
      checked: boolean;
    };
    chatGuideText: {
      checked: boolean;
      value: string | Record<string, string>;
    };
    footerBrand: {
      checked: boolean;
      value: string;
    };
    answerSource: {
      checked: boolean;
    };
    widgetColor: {
      chatColor: {
        checked: boolean;
        value: string;
      };
      sendButtonColor: {
        checked: boolean;
        value: string;
      };
      userMessageColor: {
        checked: boolean;
        value: string;
      };
      userMessageTextColor: {
        checked: boolean;
        value: string;
      };
      botMessageColor: {
        checked: boolean;
        value: string;
      };
      botMessageTextColor: {
        checked: boolean;
        value: string;
      };
    };
    chatHeaderImage: {
      checked: boolean;
      url: string;
    };
    autoOpenUrls: {
      checked: boolean;
      urls: string[];
    };
    showBotAvatar: {
      checked: boolean;
    };
  };
  chatbotAvatar: I_IconSetting;
  digitalHuman: {
    backgroundColor: {
      checked: boolean;
      value: string;
    };
    messageBackgroundOpacity: number;
    image: string;
    textureUrl?: string;
    playWelcomeMessage: {
      enabled: boolean;
      mode: 'first' | 'every';
    };
    defaultInputMode: 'text' | 'voice';
  };
}

export interface NodeItem {
  id: string;
  question: string;
  dataset_id?: string;
  lang_pack_id?: string;
  language?: string;
}

export interface WidgetFormContextProps {
  // 记录 @mantine/form 表单的实时数据
  form: UseFormReturnType<I_UserFormValues>;
  // 记录后端的配置数据，用来和表单数据做对比用。
  widgetConfigs: I_UserFormValues;
  setWidgetConfigs: Dispatch<SetStateAction<I_UserFormValues>>;
  selectedNodes: NodeItem[];
  initSelectedNode: () => void;
  setSelectedNodesWrap: (selectedNodes: NodeItem[]) => void;
  addDefaultNode: () => void;
  updateSelectedNode: (node: NodeItem, index: number) => void;
  deleteSelectedNode: (index: number) => void;
  // 新的穿梭框相关方法
  updateTransferData: (datasetId: string, nodeIds: string[]) => void;
}

const VoidFunc: any = () => {};

const WidgetFormContext = createContext<WidgetFormContextProps>({
  form: {} as UseFormReturnType<I_UserFormValues>,
  widgetConfigs: {} as I_UserFormValues,
  setWidgetConfigs: VoidFunc,

  selectedNodes: [
    {
      id: '',
      question: '',
    },
  ],
  initSelectedNode: VoidFunc,
  setSelectedNodesWrap: VoidFunc,
  addDefaultNode: VoidFunc,
  updateSelectedNode: VoidFunc,
  deleteSelectedNode: VoidFunc,
  updateTransferData: VoidFunc,
});

export const WidgetFormProvider = WidgetFormContext.Provider;
export const useWidgetFormContext = () => useContext(WidgetFormContext);
